import { sharedFetchApiClient } from "@/init/initializeInstances";
import { StoreRepositoryImpl } from "../data/repo-implement/store-repository-impl";
import { StoreApiSource } from "../data/source/store-api-source";
import { StoreMockSource } from "../data/source/store-mock-source";
import { StoreHttpClient, StoreTokenIssueResult } from "../data/store-http-client";
import { StoreURLList } from "../data/store-url-list";
import { StoreSource } from "../data/source/store-source";
import { StoreRepository } from "../domain/repository/store-repository";
import {
  CancelRefundUsecase,
  ClaimTreatmentVoucherUsecase,
  ConfirmStorePurchaseUsecase,
  DecideRefundRequestUsecase,
  ExecuteRefundUsecase,
  FetchBrandpayCardsUsecase,
  FetchBrandpayCustomerUsecase,
  FetchRefundRequestsUsecase,
  FetchStorePaymentHistoryUsecase,
  FetchStoreProductsUsecase,
  FetchStoreSettlementsUsecase,
  PrepareRefundUsecase,
  PrepareStoreOrderUsecase,
  ReclaimStoreOrderUsecase,
  RequestExceptionRefundUsecase,
  ResolveStorePatientContextUsecase,
  SearchStoreGuardianUsecase,
} from "../domain/usecase/store-usecases";
import { isStoreMockMode } from "../store-config";

/**
 * 이용권 스토어 모듈 전용 DI 컨테이너
 *
 * 기존 initializeInstances.ts 와 분리되어 있으며, 스토어 화면에서만 import 된다.
 * (스토어 모듈 → 기존 모듈 단방향 의존만 허용. 역방향 의존 금지)
 */

/**
 * store-api 호출용 클라이언트 — 스토어 전용 토큰을 의료 서버에서 발급받아 Bearer 로 사용한다.
 * (발급 요청은 로그인 쿠키가 필요하므로 기존 의료 서버 클라이언트로 보낸다)
 */
export const storeHttpClient = new StoreHttpClient(async () => {
  const response = await sharedFetchApiClient.post(StoreURLList.AUTH.STORE_TOKEN, {});
  return response.data as StoreTokenIssueResult;
});

/**
 * 목 모드에서도 환자 컨텍스트('치료 추가' 진입)는 기존 치료 목록 API(실재)를 우선 사용한다.
 * 로그인 상태면 실 환자·보호자 이름이 표시되고, 미로그인/실패 시 목 컨텍스트로 폴백한다.
 */
class StoreMockWithRealContextSource extends StoreMockSource {
  private readonly api = new StoreApiSource(sharedFetchApiClient, storeHttpClient);

  override async resolvePatientContext(patientUuid: string) {
    const real = await this.api.resolvePatientContext(patientUuid);
    if (real.isSuccess) {
      return real;
    }
    console.log(
      "[initialize-store-instances] :: 환자 컨텍스트 실 조회 실패 — 목 컨텍스트 폴백"
    );
    return super.resolvePatientContext(patientUuid);
  }
}

const storeSource: StoreSource = isStoreMockMode()
  ? new StoreMockWithRealContextSource()
  : new StoreApiSource(sharedFetchApiClient, storeHttpClient);

const storeRepository: StoreRepository = new StoreRepositoryImpl(storeSource);

export const fetchStoreProductsUsecase = new FetchStoreProductsUsecase(storeRepository);
export const fetchBrandpayCustomerUsecase = new FetchBrandpayCustomerUsecase(storeRepository);
export const fetchBrandpayCardsUsecase = new FetchBrandpayCardsUsecase(storeRepository);
export const resolveStorePatientContextUsecase = new ResolveStorePatientContextUsecase(
  storeRepository
);
export const searchStoreGuardianUsecase = new SearchStoreGuardianUsecase(storeRepository);
export const prepareStoreOrderUsecase = new PrepareStoreOrderUsecase(storeRepository);
export const confirmStorePurchaseUsecase = new ConfirmStorePurchaseUsecase(storeRepository);
export const fetchStorePaymentHistoryUsecase = new FetchStorePaymentHistoryUsecase(
  storeRepository
);
export const fetchStoreSettlementsUsecase = new FetchStoreSettlementsUsecase(storeRepository);
export const claimTreatmentVoucherUsecase = new ClaimTreatmentVoucherUsecase(storeRepository);
export const reclaimStoreOrderUsecase = new ReclaimStoreOrderUsecase(storeRepository);
export const prepareRefundUsecase = new PrepareRefundUsecase(storeRepository);
export const executeRefundUsecase = new ExecuteRefundUsecase(storeRepository);
export const cancelRefundUsecase = new CancelRefundUsecase(storeRepository);
export const requestExceptionRefundUsecase = new RequestExceptionRefundUsecase(storeRepository);
export const fetchRefundRequestsUsecase = new FetchRefundRequestsUsecase(storeRepository);
export const decideRefundRequestUsecase = new DecideRefundRequestUsecase(storeRepository);
